import styled from '@emotion/styled';
import { useCallback, useEffect, useRef } from 'react';
import { useSessionState } from '../../../context/sessionStateContext';
import { colors, fonts, breakpoints, Button, useSessionUI } from '../../../ui';

// Shared pieces of the acuity test screens (logMAR, Random E, Kay's, Near Vision).

/**
 * Mirrors the test and its sub-step into the shared session state so a re-joining clinician (and
 * the patient app) know where the session is: { step: 'test', test_key, sub_step }. Leaving the
 * test inside the app clears test_key again.
 */
export const useExamStep = (testKey, subStep) => {
  const { patch } = useSessionState();
  const patchRef = useRef(patch);
  patchRef.current = patch;
  useEffect(() => {
    if (!testKey) return;
    patchRef.current({ step: 'test', test_key: testKey, sub_step: subStep || null }).catch(() => {});
  }, [testKey, subStep]);
  useEffect(
    () => () => {
      patchRef.current({ test_key: null, sub_step: null }).catch(() => {});
    },
    [],
  );
};

const LEAVE_MESSAGE = 'Leave this test?';
const LEAVE_DETAIL = 'Navigating away from this page may mean that you lose test data.';
const RESTART_MESSAGE = 'Restart this test?';
const RESTART_DETAIL = 'All results presented here will be lost.';

/**
 * Same contract as helpers/functions confirmNavigation(): the promise resolves only when the
 * clinician confirms (it never rejects), but uses the IBISengage dialog instead of window.confirm.
 */
export const useConfirmNavigation = () => {
  const { confirm } = useSessionUI();
  return useCallback(
    (restart) =>
      new Promise((resolve) => {
        confirm({
          message: restart ? RESTART_MESSAGE : LEAVE_MESSAGE,
          detail: restart ? RESTART_DETAIL : LEAVE_DETAIL,
          yesLabel: restart ? 'Restart' : 'Leave',
          noLabel: 'Stay',
          tone: 'danger',
        }).then((ok) => ok && resolve());
      }),
    [confirm],
  );
};

// ---------------------------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------------------------

/** Full-height content box inside the panel. */
export const ExamBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  font-family: ${fonts.family};
  color: ${colors.text};
`;

/**
 * Scoring step: preview / chart on the left, controls on the right (Figma 10, 13, 16, 21, 30).
 * Stacks on phones.
 */
const layoutProp = (p) => !['fill', 'align', 'justify', 'gap', 'left', 'right'].includes(p);

export const ScoringGrid = styled('div', { shouldForwardProp: layoutProp })`
  display: grid;
  grid-template-columns: ${({ left = '1fr', right = '1fr' }) => `minmax(0, ${left}) minmax(0, ${right})`};
  gap: 24px 32px;
  flex: 1;
  min-height: 0;
  width: 100%;
  height: ${({ fill }) => (fill ? '100%' : 'auto')};
  align-items: ${({ align = 'start' }) => align};
  @media (max-width: ${breakpoints.phone - 1}px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
    height: auto;
    min-height: auto;
    flex: 0 0 auto;
  }
`;

export const Column = styled('div', { shouldForwardProp: layoutProp })`
  display: flex;
  flex-direction: column;
  gap: ${({ gap = 16 }) => gap}px;
  min-width: 0;
  min-height: 0;
  align-items: ${({ align = 'stretch' }) => align};
  justify-content: ${({ justify = 'flex-start' }) => justify};
  height: ${({ fill }) => (fill ? '100%' : 'auto')};
`;

export const Question = styled.h3`
  margin: 0;
  font-family: ${fonts.family};
  font-size: 20px;
  line-height: 1.3;
  font-weight: 700;
  color: ${colors.slate[600]};
  @media (max-width: ${breakpoints.phone - 1}px) {
    font-size: 16px;
  }
`;

export const Divider = styled.hr`
  flex-shrink: 0;
  width: 100%;
  margin: 4px 0;
  border: 0;
  border-top: 1px solid ${colors.slate[200]};
`;

export const Row = styled('div', { shouldForwardProp: layoutProp })`
  display: flex;
  flex-wrap: wrap;
  align-items: ${({ align = 'center' }) => align};
  gap: ${({ gap = 8 }) => gap}px;
  justify-content: ${({ justify = 'flex-start' }) => justify};
`;

const CountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ n }) => n}, minmax(0, 64px));
  gap: 8px;
  button {
    width: 100%;
    padding: 0;
  }
  @media (max-width: ${breakpoints.phone - 1}px) {
    grid-template-columns: repeat(${({ n }) => n}, minmax(0, 1fr));
  }
`;

/** [1] [2] [3] [4] [5] answer buttons ("How many characters seen?"). */
export const CountButtons = ({ count = 5, onPick, disabled }) => (
  <CountGrid n={count} role='group' aria-label='Number seen'>
    {Array.from({ length: count }, (_, i) => i + 1).map((n) => (
      <Button key={n} variant='secondary' size='lg' onClick={() => onPick(n)} disabled={disabled} aria-label={`${n} seen`}>
        {n}
      </Button>
    ))}
  </CountGrid>
);

/** Small labelled read-only value box ("Current logMAR Size [1]"). */
const ValueRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: ${fonts.family};
  .label {
    font-size: ${({ large }) => (large ? 18 : 12)}px;
    line-height: ${({ large }) => (large ? 1.4 : '20px')};
    font-weight: 700;
    color: ${({ large }) => (large ? colors.slate[600] : colors.slate[700])};
  }
  .value {
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${({ large }) => (large ? 100 : 80)}px;
    height: ${({ large }) => (large ? 44 : 36)}px;
    border: 1px solid ${colors.slate[200]};
    border-radius: 5px;
    background: ${colors.slate[100]};
    color: ${colors.slate[500]};
    font-family: ${fonts.family};
    font-size: 15px;
    font-variant-numeric: tabular-nums;
  }
`;

export const ValueBox = ({ label, value, large }) => (
  <ValueRoot large={large}>
    <span className='label'>{label}</span>
    <span className='value' aria-label={`${label}: ${value}`}>
      {value}
    </span>
  </ValueRoot>
);
